/**
 * Angebote — zentrale Angebotsübersicht.
 * Ersetzt die frühere Upload-Inbox (IncomingOffer/Hochladen entfernt).
 * Klick auf ein Angebot öffnet das Detail-Sheet in Aufträge (?order=…),
 * inkl. Genehmigung, PDF und "In Bestellung umwandeln".
 */
import { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, FileText, Building2, User, Calendar } from 'lucide-react';
import PageHeader from '@/components/ui/PageHeader';
import EmptyState from '@/components/ui/EmptyState';
import SkeletonCard from '@/components/ui/SkeletonCard';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { isAngebot } from '@/lib/orderFields';

function ApprovalBadge({ status }) {
  const cls = status === 'approved'
    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
    : status === 'pending_review'
      ? 'bg-orange-100 text-orange-700 border border-orange-200'
      : 'bg-slate-100 text-slate-600 border border-slate-200';
  const label = status === 'approved' ? 'Genehmigt'
    : status === 'pending_review' ? 'Prüfung ausstehend'
      : status || '—';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

export default function AdminInbox() {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['admin-all-orders'],
    queryFn: () => base44.entities.Order.list('-created_date'),
  });

  const angebote = orders.filter(isAngebot);

  const filtered = angebote.filter((o) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return o.lead_name?.toLowerCase().includes(q)
      || o.order_number?.toLowerCase().includes(q)
      || o.agent_email?.toLowerCase().includes(q);
  });

  const angeboteTotal = angebote.reduce((s, o) => s + (o.subtotal || 0), 0);
  const pendingCount = angebote.filter((o) => o.order_status === 'pending_review').length;

  // Detail-Sheet lebt in Aufträge — Deep-Link öffnet es direkt
  const openAngebot = (order) => navigate(`/admin/orders?order=${order.id}`);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <PageHeader
            icon={<FileText className="w-5 h-5" />}
            title="Angebote"
            subtitle={
              <>
                {angebote.length} Angebote
                {pendingCount > 0 && <span className="text-orange-600"> · {pendingCount} in Prüfung</span>}
              </>
            }
          />
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Suchen (Nummer, Kontakt, Agent)…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        {/* Mobile */}
        <div className="md:hidden space-y-3">
          {filtered.map((order) => (
            <Card
              key={order.id}
              className="p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => openAngebot(order)}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-mono text-sm font-medium whitespace-nowrap">{order.order_number}</p>
                  <p className="text-sm text-slate-700 mt-0.5">{order.lead_name}</p>
                </div>
                <ApprovalBadge status={order.order_status} />
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 text-sm">
                <span className="text-slate-500">
                  {order.created_date && format(new Date(order.created_date), 'dd.MM.yyyy', { locale: de })}
                </span>
                <p className="font-bold tabular-nums">
                  €{order.total?.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </Card>
          ))}
          {isLoading && filtered.length === 0 && <SkeletonCard count={4} />}
          {!isLoading && filtered.length === 0 && (
            <EmptyState
              icon={FileText}
              title="Keine Angebote"
              description="Es wurden keine Angebote gefunden."
              compact
            />
          )}
        </div>

        {/* Desktop */}
        <Card className="hidden md:block overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Nr.</TableHead>
                <TableHead>Kontakt</TableHead>
                <TableHead>Genehmigung</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead>Pos.</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead className="text-right">Betrag</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((order) => (
                <TableRow
                  key={order.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openAngebot(order)}
                >
                  <TableCell>
                    <span className="font-mono text-sm text-slate-500 whitespace-nowrap">{order.order_number}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="font-medium">{order.lead_name}</span>
                    </div>
                  </TableCell>
                  <TableCell><ApprovalBadge status={order.order_status} /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                      <Calendar className="w-3.5 h-3.5" />
                      {order.created_date && format(new Date(order.created_date), 'dd.MM.yyyy', { locale: de })}
                    </div>
                  </TableCell>
                  <TableCell>{order.items?.length || 0}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm text-slate-500">
                      <User className="w-3.5 h-3.5" />
                      <span className="truncate max-w-[140px]">{order.agent_email}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-bold tabular-nums">
                    €{order.total?.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                  </TableCell>
                </TableRow>
              ))}
              {isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-4">
                    <SkeletonCard count={3} variant="row" />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EmptyState
                      icon={FileText}
                      title="Keine Angebote"
                      description="Es wurden keine Angebote gefunden."
                      compact
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>

        <div className="mt-4 text-right text-sm text-slate-500">
          Gesamtwert Angebote (Netto):{' '}
          <span className="font-bold text-slate-800 tabular-nums">
            €{angeboteTotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
          </span>
        </div>
      </main>
    </div>
  );
}
